import Cookies from "js-cookie";
import { apiFetch } from "@/services/apiService";
import { getGuestAccessToken, invalidateGuestToken } from "@/services/guestAuthService";

/**
 * Fetch wrapper for endpoints that are reachable from public pages but still require a
 * bearer token (e.g. the fare engine on the public commute calculator).
 *
 * - Signed-in users: delegates to `apiFetch`, which attaches the cookie access token and
 *   handles 401 refresh as usual.
 * - Guests (no access-token cookie): attaches a short-lived in-memory guest token. The
 *   token is never persisted, and a single 401 retry refreshes it if it has expired.
 */
export async function publicAuthorizedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const hasUserSession = Boolean(Cookies.get("accessToken"));
  if (hasUserSession) {
    return apiFetch(url, options);
  }

  const withGuestToken = (token: string): RequestInit => ({
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    },
  });

  const token = await getGuestAccessToken();
  let response = await fetch(url, withGuestToken(token));

  if (response.status === 401) {
    invalidateGuestToken();
    const refreshedToken = await getGuestAccessToken();
    response = await fetch(url, withGuestToken(refreshedToken));
  }

  return response;
}
